/**
 * O carregamento de um relatório, num lugar só: filtro na URL, chamada, estados
 * de carregando/erro e as colunas já convertidas para o `DynamicTable`.
 *
 * DUAS ARMADILHAS DO `DynamicTable` QUE ESTE HOOK RESOLVE, e é por isso que ele
 * existe em vez de cada tela chamar o service na mão:
 *
 *  1. `rows` e `columns` TÊM que vir de `useMemo`. O efeito que inicializa a
 *     tabela depende dessas referências; um array literal inline reexecuta
 *     `initializeTableData` a cada render e o usuário perde largura de coluna,
 *     pin e colunas visíveis no meio da interação.
 *  2. O filtro mora na URL (`useSearchParams`), não em `useState`. É o que
 *     torna o link compartilhável e o botão "voltar" do navegador correto.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ColumnOption } from "../components/DynamicTable";
import { paraColunasDaTabela } from "../comum/relatorios/colunas";
import type {
  EnvelopeDeRelatorio,
  FiltroDeRelatorio,
} from "../comum/relatorios/tipos";
import { mensagemDoErro } from "../services/relatorio.service";

type Buscador = (filtro: FiltroDeRelatorio) => Promise<EnvelopeDeRelatorio>;

export interface RelatorioCarregado {
  envelope: EnvelopeDeRelatorio | null;
  linhas: any[];
  colunas: ColumnOption[];
  carregando: boolean;
  erro: string | null;
  /** Os valores atuais do filtro, lidos da URL. */
  filtro: Record<string, string>;
  /** Escreve na URL — o que dispara a recarga. */
  mudarFiltro: (campo: string, valor: string) => void;
  limparFiltro: () => void;
  recarregar: () => void;
}

export function useRelatorio(
  buscar: Buscador,
  /** Campos do filtro que esta tela usa. Só eles saem da URL. */
  campos: string[],
  /** Filtro fixo da tela (aba, canal da visão) — some da URL, entra na chamada. */
  extras: FiltroDeRelatorio = {}
): RelatorioCarregado {
  const [parametrosDaUrl, definirParametros] = useSearchParams();
  const [envelope, setEnvelope] = useState<EnvelopeDeRelatorio | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [gatilho, setGatilho] = useState(0);

  const filtro = useMemo(() => {
    const atual: Record<string, string> = {};
    campos.forEach((campo) => {
      atual[campo] = parametrosDaUrl.get(campo) ?? "";
    });
    return atual;
    // `campos` é literal no chamador; comparar pela chave evita depender da
    // identidade do array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametrosDaUrl, campos.join(",")]);

  // Serializado: `extras` costuma ser um objeto literal no chamador, e
  // depender da identidade dele reexecutaria a busca a cada render.
  const extrasSerializados = JSON.stringify(extras);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro(null);

    buscar({ ...filtro, ...JSON.parse(extrasSerializados) })
      .then((resposta) => {
        if (!cancelado) setEnvelope(resposta);
      })
      .catch((problema) => {
        if (!cancelado) {
          setErro(mensagemDoErro(problema));
          setEnvelope(null);
        }
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [buscar, filtro, extrasSerializados, gatilho]);

  const mudarFiltro = useCallback(
    (campo: string, valor: string) => {
      const proximos = new URLSearchParams(parametrosDaUrl);
      if (valor) proximos.set(campo, valor);
      else proximos.delete(campo);
      // Trocar o filtro volta para a primeira página: manter a página 7 de um
      // recorte que agora tem 2 páginas mostraria uma tela vazia sem motivo.
      proximos.delete("pagina");
      definirParametros(proximos);
    },
    [parametrosDaUrl, definirParametros]
  );

  const limparFiltro = useCallback(() => {
    definirParametros(new URLSearchParams());
  }, [definirParametros]);

  // Os dois `useMemo` que o DynamicTable exige (ver o cabeçalho).
  const linhas = useMemo(() => envelope?.linhas ?? [], [envelope]);
  const colunas = useMemo(
    () => paraColunasDaTabela(envelope?.colunas ?? []),
    [envelope]
  );

  return {
    envelope,
    linhas,
    colunas,
    carregando,
    erro,
    filtro,
    mudarFiltro,
    limparFiltro,
    recarregar: () => setGatilho((n) => n + 1),
  };
}
